import {
  Box,
  Button,
  Container,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Toolbar,
  Typography,
} from "@mui/material";
import DevicesOtherIcon from "@mui/icons-material/DevicesOther";
import PhoneIcon from "@mui/icons-material/Phone";
import PlaceIcon from "@mui/icons-material/Place";
import SettingsRemoteIcon from "@mui/icons-material/SettingsRemote";
import TvIcon from "@mui/icons-material/Tv";
import { Map, Placemark, YMaps } from "react-yandex-maps";
import { AppToolbar } from "./AppToolbar";

const PHONE_HREF = "tel:+79040289741";
const PHONE_LABEL = "+7 (904) 028-97-41";
const ADDRESS = "г. Тверь, пр. Октябрьский, 99, секция 4";
const MAP_CENTER = [56.817074, 35.889125];

const copperRule = {
  width: 48,
  height: 3,
  bgcolor: "primary.main",
  borderRadius: 1,
  mt: 1.5,
  mb: 3,
};

function SectionHeading({ children }) {
  return (
    <>
      <Typography variant="h2">{children}</Typography>
      <Box sx={{ ...copperRule, mb: 2 }} />
    </>
  );
}

function App() {
  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <AppToolbar />
      <Toolbar sx={{ minHeight: { xs: 64, sm: 72 } }} />

      <Container
        component="main"
        maxWidth="md"
        sx={{ flex: 1, py: { xs: 4, md: 7 } }}
      >
        <Box sx={{ mb: { xs: 5, md: 8 }, maxWidth: 640 }}>
          <Typography variant="h1">Телемастерская в Южном</Typography>
          <Box sx={copperRule} />
          <Typography color="text.secondary" sx={{ mb: 1.5 }}>
            Время работы: с 13:00 до 18:30. По договоренности время работы может
            быть изменено.
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            Перед посещением позвоните, чтобы убедиться, что мастер на месте.
          </Typography>
          <Button
            size="large"
            variant="contained"
            href={PHONE_HREF}
            startIcon={<PhoneIcon />}
          >
            Позвонить
          </Button>
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: { xs: 2.5, sm: 3.5 }, height: "100%" }}>
              <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 1 }}>
                <TvIcon color="primary" />
                <Typography variant="h2">Услуги</Typography>
              </Stack>
              <Box sx={{ ...copperRule, mb: 1 }} />
              <List disablePadding>
                <ListItem disableGutters>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <TvIcon fontSize="small" color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Ремонт LCD-телевизоров. Низкие цены на ремонт." />
                </ListItem>
                <ListItem disableGutters>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <SettingsRemoteIcon fontSize="small" color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Ремонт пультов дистанционного управления" />
                </ListItem>
                <ListItem disableGutters>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <DevicesOtherIcon fontSize="small" color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Ремонт бытовой электроники" />
                </ListItem>
              </List>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper sx={{ p: { xs: 2.5, sm: 3.5 }, height: "100%" }}>
              <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 1 }}>
                <SettingsRemoteIcon color="primary" />
                <Typography variant="h2">Продаём</Typography>
              </Stack>
              <Box sx={{ ...copperRule, mb: 1 }} />
              <List disablePadding>
                <ListItem disableGutters>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <TvIcon fontSize="small" color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Подержанные телевизоры." />
                </ListItem>
                <ListItem disableGutters>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <SettingsRemoteIcon fontSize="small" color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Пульты дистанционного управления" />
                </ListItem>
              </List>
            </Paper>
          </Grid>
        </Grid>

        <Typography color="text.secondary" sx={{ mt: 2.5, mb: { xs: 5, md: 8 } }}>
          Оплату принимаем наличными, либо онлайн переводом на счёт в Сбербанке.
        </Typography>

        <SectionHeading>Контакты</SectionHeading>
        <Paper sx={{ p: { xs: 2.5, sm: 3.5 }, mb: 3 }}>
          <Stack spacing={2}>
            <Stack direction="row" spacing={1.5} alignItems="flex-start">
              <PhoneIcon color="primary" sx={{ mt: 0.25 }} />
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Телефон
                </Typography>
                <Link
                  href={PHONE_HREF}
                  underline="none"
                  color="text.primary"
                  sx={{ fontWeight: 600, fontSize: "1.15rem" }}
                >
                  {PHONE_LABEL}
                </Link>
              </Box>
            </Stack>
            <Stack direction="row" spacing={1.5} alignItems="flex-start">
              <PlaceIcon color="primary" sx={{ mt: 0.25 }} />
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Адрес нашей мастерской
                </Typography>
                <Typography sx={{ fontWeight: 600 }}>{ADDRESS}</Typography>
              </Box>
            </Stack>
          </Stack>
        </Paper>

        <Box
          sx={{
            overflow: "hidden",
            borderRadius: 2,
            border: 1,
            borderColor: "divider",
            mb: 3,
          }}
        >
          <YMaps>
            <Map
              width="100%"
              height={400}
              defaultState={{ center: MAP_CENTER, zoom: 13 }}
            >
              <Placemark defaultGeometry={MAP_CENTER} />
            </Map>
          </YMaps>
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 2,
          }}
        >
          <Box
            component="img"
            src="/dom1.jpg"
            alt="Фото дома"
            sx={{
              width: "100%",
              height: { xs: 220, sm: 280 },
              objectFit: "cover",
              borderRadius: 2,
              border: 1,
              borderColor: "divider",
              bgcolor: "background.paper",
            }}
          />
          <Box
            component="img"
            src="/dver1.jpg"
            alt="Фото входной двери"
            sx={{
              width: "100%",
              height: { xs: 220, sm: 280 },
              objectFit: "cover",
              borderRadius: 2,
              border: 1,
              borderColor: "divider",
              bgcolor: "background.paper",
            }}
          />
        </Box>
      </Container>

      <Box
        component="footer"
        sx={{
          borderTop: 1,
          borderColor: "divider",
          bgcolor: "background.paper",
          py: 3,
          mt: 6,
        }}
      >
        <Container>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "center" }}
          >
            <Typography variant="h3" sx={{ fontSize: "1rem" }}>
              Телемастерская в Южном
            </Typography>
            <Typography color="text.secondary">
              <Link href={PHONE_HREF} underline="none" color="inherit">
                {PHONE_LABEL}
              </Link>
              {" · "}
              {ADDRESS}
            </Typography>
          </Stack>
        </Container>
      </Box>
    </Box>
  );
}

export default App;
